import { canReconcileTerminals } from '../helpers/terminal-reconciliation.helpers';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authSelector } from '../store/slices';
import { useAppSelector } from './redux.hooks';

export const usePermissions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector(authSelector);

  const operationsRoutes = React.useMemo(
    () => [
      '/dashboard/brand',
      '/dashboard/brands',
      '/dashboard/food-items',
      '/dashboard/order',
      '/dashboard/food-groups',
      '/dashboard/options',
      '/dashboard/abandoned-checkout',
      '/dashboard/abandoned-checkout',
      '/dashboard/pilot',
      '/dashboard/pilots',
    ],
    []
  );

  const initiatorRoutes = React.useMemo(
    () => [
      '/dashboard/users',
      '/dashboard/user/',
      '/dashboard/wallet-transactions',
    ],
    []
  );

  const hiddenMarketingRoutes = React.useMemo(() => ['/dashboard/'], []);

  const routeAdmin = React.useCallback(() => {
    if (
      location.pathname.startsWith('/dashboard/terminal-reconciliation') &&
      canReconcileTerminals(user?.admin)
    )
      return;
    if (user?.admin?.roles.includes('OPERATIONS') && user?.admin.kitchens) {
      if (
        !operationsRoutes.some((route) => location.pathname.includes(route)) &&
        location.pathname !== '/dashboard/'
      ) {
        return navigate('/dashboard/orders');
      }
    }

    if (user?.admin?.roles.includes('OPERATIONS') && !user?.admin.kitchens) {
      if (!operationsRoutes.some((route) => location.pathname.includes(route)))
        return navigate('/dashboard/orders');
    }

    if (user?.admin?.roles.includes('MARKETING')) {
      if (hiddenMarketingRoutes.some((route) => location.pathname === route))
        return navigate('/dashboard/brands');
    }

    if (user?.admin?.roles.includes('INITIATOR')) {
      if (!initiatorRoutes.some((route) => location.pathname.includes(route)))
        return navigate('/dashboard/users');
    }
  }, [
    user,
    navigate,
    initiatorRoutes,
    operationsRoutes,
    location.pathname,
    hiddenMarketingRoutes,
  ]);

  const isOwner = React.useMemo(
    () => user?.admin?.roles.includes('OWNER'),
    [user]
  );

  const isInitiator = React.useMemo(
    () => user?.admin?.roles.includes('INITIATOR'),
    [user]
  );

  const isCommunityManager = !!user?.admin?.roles.includes('COMMUNITY_MANAGER');

  const userRole = React.useMemo(() => user?.admin?.roles, [user]);

  return {
    isOwner,
    isCommunityManager,
    userRole,
    isInitiator,
    routeAdmin,
  };
};
